import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import plantillasApi from '../../../api/plantillasApi';

/**
 * Componente para gestión de plantillas de proceso
 * Solo para Admin del Sistema
 * Permite ver y crear plantillas predefinidas
 */
const GestionPlantillas = () => {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDetalleDialog, setOpenDetalleDialog] = useState(false);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
  const [plantillaEditar, setPlantillaEditar] = useState(null);
  const [procesando, setProcesando] = useState(false);

  // Estado para nueva plantilla
  const [nuevaPlantilla, setNuevaPlantilla] = useState({
    nombre_plantilla: '',
    descripcion: '',
    soporte_gis: false,
    estados_flujo_ids: []
  });

  // Estado para plantilla en edición
  const [plantillaEditada, setPlantillaEditada] = useState({
    nombre_plantilla: '',
    descripcion: '',
    soporte_gis: false,
    estados_flujo_ids: []
  });

  // Estados disponibles del sistema (desde BD)
  const estadosDisponibles = [
    { id: 1, nombre: 'Pendiente' },
    { id: 2, nombre: 'Asignado' },
    { id: 3, nombre: 'En Camino' },
    { id: 4, nombre: 'En Proceso' },
    { id: 5, nombre: 'Terminado' },
    { id: 6, nombre: 'En espera' },
    { id: 7, nombre: 'Cancelado' },
    { id: 8, nombre: 'Firmado' },
    { id: 9, nombre: 'Revisado' },
    { id: 10, nombre: 'Liquidado' }
  ];

  /**
   * Cargar plantillas desde el backend
   */
  const loadPlantillas = async () => {
    try {
      setLoading(true);
      const response = await plantillasApi.getPlantillas();
      setPlantillas(response.data || []);
      setMensaje(null);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
      setMensaje({
        type: 'error',
        text: 'Error al cargar plantillas'
      });
    } finally {
      setLoading(false);
    }
  };

  // Cargar plantillas al montar
  useEffect(() => {
    loadPlantillas();
  }, []);

  /**
   * Manejar creación de nueva plantilla
   */
  const handleCrearPlantilla = async () => {
    // Validaciones básicas
    if (!nuevaPlantilla.nombre_plantilla.trim()) {
      setMensaje({
        type: 'error',
        text: 'El nombre de la plantilla es obligatorio'
      });
      return;
    }

    if (nuevaPlantilla.estados_flujo_ids.length === 0) {
      setMensaje({
        type: 'error',
        text: 'Debe seleccionar al menos un estado'
      });
      return;
    }

    try {
      setProcesando(true);
      await plantillasApi.createPlantilla(nuevaPlantilla);

      setMensaje({
        type: 'success',
        text: 'Plantilla creada correctamente'
      });

      setOpenDialog(false);
      setNuevaPlantilla({
        nombre_plantilla: '',
        descripcion: '',
        soporte_gis: false,
        estados_flujo_ids: []
      });

      await loadPlantillas();
    } catch (error) {
      console.error('Error creando plantilla:', error);
      setMensaje({
        type: 'error',
        text: error.response?.data?.error || 'Error al crear plantilla'
      });
    } finally {
      setProcesando(false);
    }
  };

  /**
   * Ver detalle de plantilla
   */
  const handleVerDetalle = async (idPlantilla) => {
    try {
      const response = await plantillasApi.getPlantilla(idPlantilla);
      setPlantillaSeleccionada(response.data);
      setOpenDetalleDialog(true);
    } catch (error) {
      console.error('Error obteniendo detalle:', error);
      setMensaje({
        type: 'error',
        text: 'Error al obtener detalle de plantilla'
      });
    }
  };

  /**
   * Abrir diálogo para editar plantilla
   */
  const handleAbrirEditar = async (idPlantilla) => {
    try {
      const response = await plantillasApi.getPlantilla(idPlantilla);
      const plantilla = response.data;

      setPlantillaEditar(plantilla);
      setPlantillaEditada({
        nombre_plantilla: plantilla.nombre_plantilla,
        descripcion: plantilla.descripcion || '',
        soporte_gis: plantilla.soporte_gis || false,
        estados_flujo_ids: plantilla.estados_flujo_ids || []
      });
      setOpenEditDialog(true);
    } catch (error) {
      console.error('Error obteniendo plantilla:', error);
      setMensaje({
        type: 'error',
        text: 'Error al obtener plantilla para editar'
      });
    }
  };

  /**
   * Manejar actualización de plantilla
   */
  const handleActualizarPlantilla = async () => {
    // Validaciones básicas
    if (!plantillaEditada.nombre_plantilla.trim()) {
      setMensaje({
        type: 'error',
        text: 'El nombre de la plantilla es obligatorio'
      });
      return;
    }

    if (plantillaEditada.estados_flujo_ids.length === 0) {
      setMensaje({
        type: 'error',
        text: 'Debe seleccionar al menos un estado'
      });
      return;
    }

    try {
      setProcesando(true);
      await plantillasApi.updatePlantilla(plantillaEditar.id_plantilla, plantillaEditada);

      setMensaje({
        type: 'success',
        text: 'Plantilla actualizada correctamente'
      });

      setOpenEditDialog(false);
      setPlantillaEditar(null);
      setPlantillaEditada({
        nombre_plantilla: '',
        descripcion: '',
        soporte_gis: false,
        estados_flujo_ids: []
      });

      await loadPlantillas();
    } catch (error) {
      console.error('Error actualizando plantilla:', error);
      setMensaje({
        type: 'error',
        text: error.response?.data?.error || 'Error al actualizar plantilla'
      });
    } finally {
      setProcesando(false);
    }
  };

  /**
   * Manejar selección de estados (nueva plantilla)
   */
  const handleEstadosChange = (event) => {
    setNuevaPlantilla({
      ...nuevaPlantilla,
      estados_flujo_ids: event.target.value
    });
  };

  /**
   * Manejar selección de estados (plantilla en edición)
   */
  const handleEstadosChangeEdit = (event) => {
    setPlantillaEditada({
      ...plantillaEditada,
      estados_flujo_ids: event.target.value
    });
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestión de Plantillas
        </Typography>
        <Box>
          <Tooltip title="Recargar">
            <IconButton onClick={loadPlantillas} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{ ml: 1 }}
          >
            Nueva Plantilla
          </Button>
        </Box>
      </Box>

      {/* Mensajes */}
      {mensaje && (
        <Alert
          severity={mensaje.type}
          onClose={() => setMensaje(null)}
          sx={{ mb: 3 }}
        >
          {mensaje.text}
        </Alert>
      )}

      {/* Tabla de plantillas */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nombre</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Soporte GIS</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Estados</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plantillas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary" py={3}>
                      No hay plantillas creadas
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                plantillas.map((plantilla) => (
                  <TableRow key={plantilla.id_plantilla} hover>
                    <TableCell>{plantilla.id_plantilla}</TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {plantilla.nombre_plantilla}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {plantilla.descripcion || 'Sin descripción'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plantilla.soporte_gis ? 'Sí' : 'No'}
                        size="small"
                        color={plantilla.soporte_gis ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${plantilla.estados_flujo_ids?.length || 0} estados`}
                        size="small"
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar plantilla">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleAbrirEditar(plantilla.id_plantilla)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ver detalle">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleVerDetalle(plantilla.id_plantilla)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog: Crear Plantilla */}
      <Dialog
        open={openDialog}
        onClose={() => !procesando && setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Crear Nueva Plantilla
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre de la Plantilla"
                value={nuevaPlantilla.nombre_plantilla}
                onChange={(e) =>
                  setNuevaPlantilla({ ...nuevaPlantilla, nombre_plantilla: e.target.value })
                }
                required
                helperText="Ejemplo: Servicios Fijos, Servicios Móviles, Express"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                multiline
                rows={3}
                value={nuevaPlantilla.descripcion}
                onChange={(e) =>
                  setNuevaPlantilla({ ...nuevaPlantilla, descripcion: e.target.value })
                }
                helperText="Breve descripción de para qué tipo de negocio es esta plantilla"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={nuevaPlantilla.soporte_gis}
                    onChange={(e) =>
                      setNuevaPlantilla({ ...nuevaPlantilla, soporte_gis: e.target.checked })
                    }
                    color="primary"
                  />
                }
                label="Soporte para Geolocalización (GIS)"
              />
              <Typography variant="caption" color="textSecondary" display="block">
                Habilita el uso de coordenadas GPS en las órdenes de servicio
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Estados del Flujo</InputLabel>
                <Select
                  multiple
                  value={nuevaPlantilla.estados_flujo_ids}
                  onChange={handleEstadosChange}
                  label="Estados del Flujo"
                  renderValue={(selected) => (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {selected.map((id) => {
                        const estado = estadosDisponibles.find((e) => e.id === id);
                        return <Chip key={id} label={estado?.nombre || id} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {estadosDisponibles.map((estado) => (
                    <MenuItem key={estado.id} value={estado.id}>
                      {estado.nombre}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                  Selecciona los estados que formarán parte del flujo de esta plantilla
                </Typography>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={procesando}>
            Cancelar
          </Button>
          <Button
            onClick={handleCrearPlantilla}
            variant="contained"
            color="primary"
            disabled={procesando}
            startIcon={procesando ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {procesando ? 'Creando...' : 'Crear Plantilla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Editar Plantilla */}
      <Dialog
        open={openEditDialog}
        onClose={() => !procesando && setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Editar Plantilla
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre de la Plantilla"
                value={plantillaEditada.nombre_plantilla}
                onChange={(e) =>
                  setPlantillaEditada({ ...plantillaEditada, nombre_plantilla: e.target.value })
                }
                required
                helperText="Ejemplo: Servicios Fijos, Servicios Móviles, Express"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                multiline
                rows={3}
                value={plantillaEditada.descripcion}
                onChange={(e) =>
                  setPlantillaEditada({ ...plantillaEditada, descripcion: e.target.value })
                }
                helperText="Breve descripción de para qué tipo de negocio es esta plantilla"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={plantillaEditada.soporte_gis}
                    onChange={(e) =>
                      setPlantillaEditada({ ...plantillaEditada, soporte_gis: e.target.checked })
                    }
                    color="primary"
                  />
                }
                label="Soporte para Geolocalización (GIS)"
              />
              <Typography variant="caption" color="textSecondary" display="block">
                Habilita el uso de coordenadas GPS en las órdenes de servicio
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Estados del Flujo</InputLabel>
                <Select
                  multiple
                  value={plantillaEditada.estados_flujo_ids}
                  onChange={handleEstadosChangeEdit}
                  label="Estados del Flujo"
                  renderValue={(selected) => (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {selected.map((id) => {
                        const estado = estadosDisponibles.find((e) => e.id === id);
                        return <Chip key={id} label={estado?.nombre || id} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {estadosDisponibles.map((estado) => (
                    <MenuItem key={estado.id} value={estado.id}>
                      {estado.nombre}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                  Selecciona los estados que formarán parte del flujo de esta plantilla
                </Typography>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} disabled={procesando}>
            Cancelar
          </Button>
          <Button
            onClick={handleActualizarPlantilla}
            variant="contained"
            color="primary"
            disabled={procesando}
            startIcon={procesando ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {procesando ? 'Actualizando...' : 'Actualizar Plantilla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Ver Detalle */}
      <Dialog
        open={openDetalleDialog}
        onClose={() => setOpenDetalleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Detalle de Plantilla</DialogTitle>
        <DialogContent dividers>
          {plantillaSeleccionada && (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {plantillaSeleccionada.nombre_plantilla}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {plantillaSeleccionada.descripcion || 'Sin descripción'}
              </Typography>

              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Soporte GIS:</strong>{' '}
                  <Chip
                    label={plantillaSeleccionada.soporte_gis ? 'Sí' : 'No'}
                    size="small"
                    color={plantillaSeleccionada.soporte_gis ? 'success' : 'default'}
                    variant="outlined"
                  />
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <strong>Estados del Flujo:</strong>
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {plantillaSeleccionada.estados_flujo_ids?.map((idEstado) => {
                    const estado = estadosDisponibles.find((e) => e.id === idEstado);
                    return (
                      <Chip
                        key={idEstado}
                        label={estado?.nombre || `Estado ${idEstado}`}
                        size="small"
                        color="primary"
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetalleDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionPlantillas;
